import { useState } from "react";
import {
  QrCode, Loader2, Copy, Send, Mail, Smartphone, Key,
  RefreshCw, CheckCircle2, ExternalLink as LinkExternal
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../../components/ui/dialog";
import { API } from "./helpers";

export function QRCodeDAL({ dossierId, tenantId }) {
  const [show, setShow] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/dal/qrcode/${dossierId}`, {
        headers: { Authorization: `Bearer ${token}`, "X-Tenant-ID": tenantId || "tef" }
      });
      if (res.ok) setQrData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleOpen = () => { setShow(true); generate(); };
  const copyLink = () => { if (qrData?.dal_url) navigator.clipboard.writeText(qrData.dal_url); };
  const shareWhatsApp = () => {
    if (!qrData) return;
    const text = encodeURIComponent(`DAL Mobile - ${qrData.client_nom}\nCode: ${qrData.référence}\nPIN: ${qrData.pin}\n${qrData.dal_url}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };
  const shareEmail = () => {
    if (!qrData) return;
    const subject = encodeURIComponent(`DAL Mobile - ${qrData.client_nom}`);
    const body = encodeURIComponent(`Bonjour,\n\nVoici le lien pour accéder au DAL mobile :\n\nCode: ${qrData.référence}\nPIN: ${qrData.pin}\nLien: ${qrData.dal_url}\n\nCordialement`);
    window.open(`mailto:?subject=${subject}&body=${body}`, "_self");
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={handleOpen} className="border-[var(--oh-stat-cyan-border)] text-[var(--oh-stat-cyan-text)] hover:bg-[var(--oh-stat-cyan-bg)]" data-testid="qr-code-dal-btn">
        <QrCode className="w-4 h-4 mr-1" /> QR
      </Button>
      {show && (
        <div className="fixed inset-0 bg-[var(--oh-bg)] z-50 flex items-center justify-center p-4" onClick={() => setShow(false)}>
          <div className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()} data-testid="qr-code-dialog">
            <h3 className="text-[var(--oh-text)] font-medium text-lg mb-4 flex items-center gap-2"><QrCode className="w-5 h-5 text-cyan-400" /> QR Code DAL Mobile</h3>
            {loading ? (
              <div className="text-center py-8"><Loader2 className="w-8 h-8 animate-spin text-cyan-400 mx-auto" /></div>
            ) : qrData ? (
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg flex justify-center">
                  <img src={`data:image/png;base64,${qrData.qr_image}`} alt="QR Code" className="w-48 h-48" />
                </div>
                <div className="text-center">
                  <p className="text-[var(--oh-text)] font-medium">{qrData.client_nom}</p>
                  <p className="text-[var(--oh-text-secondary)] text-sm">Code: {qrData.référence} | PIN: {qrData.pin}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  <Button size="sm" variant="outline" onClick={copyLink} className="border-[var(--oh-border)] text-xs" data-testid="qr-copy-link"><Copy className="w-3 h-3 mr-1" /> Copier</Button>
                  <Button size="sm" onClick={shareWhatsApp} className="bg-green-600 hover:bg-green-700 text-xs" data-testid="qr-share-whatsapp"><Send className="w-3 h-3 mr-1" /> WhatsApp</Button>
                  <Button size="sm" variant="outline" onClick={shareEmail} className="border-[var(--oh-border)] text-xs" data-testid="qr-share-email"><Mail className="w-3 h-3 mr-1" /> Email</Button>
                </div>
              </div>
            ) : (
              <p className="text-[var(--oh-error-text)] text-center py-4">Erreur de generation</p>
            )}
            <Button variant="ghost" size="sm" onClick={() => setShow(false)} className="w-full mt-3 text-[var(--oh-text-secondary)]">Fermer</Button>
          </div>
        </div>
      )}
    </>
  );
}

export function MobilePINManager({ dossierId, tenantId, dossierRef }) {
  const [showDialog, setShowDialog] = useState(false);
  const [pin, setPin] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadCurrentPIN = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/dal/pin/${dossierId}`, {
        headers: { "X-Tenant-ID": tenantId || "tef", ...(token && { "Authorization": `Bearer ${token}` }) }
      });
      if (res.ok) { const data = await res.json(); setPin(data.pin || null); }
    } catch (err) { console.error("Erreur chargement PIN:", err); }
    finally { setLoading(false); }
  };

  const generateNewPIN = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/dal/generate-pin/${dossierId}`, {
        method: 'POST',
        headers: { "X-Tenant-ID": tenantId || "tef", ...(token && { "Authorization": `Bearer ${token}` }) }
      });
      if (res.ok) { const data = await res.json(); setPin(data.pin); }
    } catch (err) { console.error("Erreur generation PIN:", err); }
    finally { setGenerating(false); }
  };

  const copyAccessInfo = () => {
    const baseUrl = window.location.origin;
    const accessInfo = `URL: ${baseUrl}/m/dal/${dossierId}\nCode dossier: ${dossierRef || dossierId.slice(-8).toUpperCase()}\nPIN: ${pin || '(a générer)'}`;
    navigator.clipboard.writeText(accessInfo);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpen = () => { setShowDialog(true); loadCurrentPIN(); };
  const mobileUrl = `${window.location.origin}/m/dal/${dossierId}`;

  return (
    <>
      <Button variant="outline" size="sm" onClick={handleOpen} className="border-[var(--oh-stat-cyan-border)] text-[var(--oh-stat-cyan-text)] hover:bg-[var(--oh-stat-cyan-bg)]" data-testid="mobile-pin-btn">
        <Smartphone className="w-4 h-4 sm:mr-1" />
        <span className="hidden sm:inline">Acces Mobile</span>
      </Button>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-white dark:bg-[var(--oh-bg-surface)] border-[var(--oh-border)] dark:border-[var(--oh-border)] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)] dark:text-[var(--oh-text)] flex items-center gap-2">
              <Key className="w-5 h-5 text-cyan-500 dark:text-cyan-400" /> Acces Mobile DAL
            </DialogTitle>
            <DialogDescription className="text-[var(--oh-text-muted)] dark:text-[var(--oh-text-secondary)]">
              Partagez ces informations avec le visiteur terrain pour acceder au formulaire DAL depuis un mobile.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-[var(--oh-bg-surface)] dark:bg-[var(--oh-bg-elevated)] rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[var(--oh-text-muted)] dark:text-[var(--oh-text-muted)] text-sm">Code dossier</span>
                <span className="text-[var(--oh-text)] dark:text-[var(--oh-text)] font-mono font-medium">{dossierRef || dossierId.slice(-8).toUpperCase()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[var(--oh-text-muted)] dark:text-[var(--oh-text-muted)] text-sm">PIN d'acces</span>
                <div className="flex items-center gap-2">
                  {loading ? <RefreshCw className="w-4 h-4 text-[var(--oh-text-secondary)] dark:text-[var(--oh-text-muted)] animate-spin" />
                    : pin ? <span className="text-cyan-600 dark:text-cyan-400 font-mono font-medium text-lg tracking-wider">{pin}</span>
                    : <span className="text-[var(--oh-text-secondary)] dark:text-[var(--oh-text-secondary)] italic">Non defini</span>}
                </div>
              </div>
              <div className="pt-2 border-t border-[var(--oh-border)] dark:border-[var(--oh-border)]">
                <p className="text-[var(--oh-text-muted)] dark:text-[var(--oh-text-muted)] text-xs mb-2">URL d'acces mobile</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-[var(--oh-bg-surface)] dark:bg-[var(--oh-bg-elevated)] px-2 py-1 rounded text-cyan-700 dark:text-cyan-300 break-all leading-relaxed">{mobileUrl}</code>
                  <Button variant="ghost" size="sm" onClick={() => window.open(mobileUrl, '_blank')} className="h-7 w-7 p-0" title="Ouvrir">
                    <LinkExternal className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" className="flex-1 text-sm" onClick={generateNewPIN} disabled={generating}>
                {generating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                {pin ? 'Regenerer PIN' : 'Générer PIN'}
              </Button>
              <Button className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-sm" onClick={copyAccessInfo} disabled={!pin}>
                {copied ? <><CheckCircle2 className="w-4 h-4 mr-2" /> Copie !</> : <><Copy className="w-4 h-4 mr-2" /> Copier infos</>}
              </Button>
            </div>
            <p className="text-[var(--oh-text-secondary)] text-xs text-center">Le visiteur pourra saisir les informations DAL directement depuis son smartphone.</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
